/**
 * Quote Request Viewer Component
 * Displays complete quote request details from temporary order data
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Email as EmailIcon,
  AttachFile as AttachFileIcon,
  GetApp as DownloadIcon,
  Visibility as PreviewIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  Description as DescriptionIcon,
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon,
  Business as BusinessIcon,
  History as HistoryIcon
} from '@mui/icons-material';

import { temporaryOrderService, type TemporaryOrder, type TemporaryOrderPosition, type PositionFile } from '../../../services/temporaryOrderService';
import { caseManagementService, type CaseData } from '../../../services/caseManagementService';
import { useNotification } from '../../../contexts/NotificationContext';
import { AutomaticWorkflowService } from '../../../services/automaticWorkflowService';

interface QuoteRequestViewerProps {
  caseId: string;
  caseNumber?: string;
  caseData?: CaseData | null; // NEW: Optional case data for supplier info
  currentWorkflowState?: string;
  onEmailSent?: () => void;
  onRefresh?: () => void;
}

export const QuoteRequestViewer: React.FC<QuoteRequestViewerProps> = ({
  caseId,
  caseNumber,
  caseData,
  currentWorkflowState,
  onEmailSent,
  onRefresh
}) => {
  const [temporaryOrder, setTemporaryOrder] = useState<TemporaryOrder | null>(null);
  const [caseDetails, setCaseDetails] = useState<CaseData | null>(caseData || null);
  const [loading, setLoading] = useState(true);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailContent, setEmailContent] = useState<any>(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [expandedPanels, setExpandedPanels] = useState<Set<string>>(new Set(['positions']));
  const [emails, setEmails] = useState<any[]>([]);
  const [loadingEmails, setLoadingEmails] = useState(false);
  
  const { showNotification } = useNotification();

  useEffect(() => {
    loadTemporaryOrder();
    loadEmailHistory();
    // Always load case data to ensure we have the latest metadata with suppliers
    loadCaseData();
  }, [caseId, caseData]);

  const loadCaseData = async () => {
    try {
      const response = await caseManagementService.getCaseDetails(caseId);
      if (response.success && response.data) {
        console.log('🔍 QuoteRequestViewer loadCaseData response:', response.data);
        console.log('🎯 Case metadata suppliers:', response.data.case?.metadata?.suppliers);
        setCaseDetails(response.data.case);
      }
    } catch (error: any) {
      console.error('Error loading case data:', error);
      // Non-critical error - component still functions without supplier info
    }
  };

  const loadEmailHistory = async () => {
    if (!caseId) return;
    
    setLoadingEmails(true);
    try {
      const response = await caseManagementService.getCaseEmails(caseId);
      if (response.success && response.data) {
        setEmails(response.data);
      } else {
        showNotification('Failed to load email history', 'error');
      }
    } catch (error: any) {
      console.error('Error loading case emails:', error);
      showNotification(`Error loading email history: ${error.message}`, 'error');
    } finally {
      setLoadingEmails(false);
    }
  };

  const loadTemporaryOrder = async () => {
    setLoading(true);
    try {
      const response = await temporaryOrderService.getTemporaryOrderByCase(caseId);
      if (response.success && response.data) {
        setTemporaryOrder(response.data);
      } else {
        showNotification(response.error || 'Failed to load quote request details', 'error');
      }
    } catch (error: any) {
      showNotification(`Error loading quote request: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePanelToggle = (panel: string) => {
    const newExpanded = new Set(expandedPanels);
    if (newExpanded.has(panel)) {
      newExpanded.delete(panel);
    } else {
      newExpanded.add(panel);
    }
    setExpandedPanels(newExpanded);
  };

  const generateEmailPreview = async () => {
    try {
      const response = await temporaryOrderService.generateQuoteRequestEmail(caseId);
      if (response.success && response.data) {
        setEmailContent(response.data);
        setEmailDialogOpen(true);
        
        // DEBUG: Log current workflow state before attempting transition
        console.log(`🔍 DEBUG: Current workflow state: "${currentWorkflowState}"`);
        console.log(`🔍 DEBUG: Expected state for transition: "supplier_quote_generated"`);
        console.log(`🔍 DEBUG: State match: ${currentWorkflowState === 'supplier_quote_generated'}`);
        
        // Trigger automatic workflow transition for supplier_quote_generated → supplier_contacted
        if (currentWorkflowState === 'supplier_quote_generated') {
          console.log(`🚀 Attempting auto-transition: supplier_quote_generated → supplier_contacted (Preview Email clicked)`);
          try {
            const success = await AutomaticWorkflowService.triggerSupplierEvent(
              caseId,
              currentWorkflowState,
              'EMAIL_PREVIEW_GENERATED',
              {
                action: 'email_preview_generated',
                emailData: {
                  subject: response.data.subject,
                  recipients: response.data.to?.length || 0,
                  attachments: response.data.attachments?.length || 0
                },
                timestamp: new Date().toISOString()
              }
            );
            
            if (success) {
              console.log(`✅ Auto-transition completed: supplier_quote_generated → supplier_contacted`);
              // Call onRefresh to update the UI with new workflow state
              onRefresh?.();
            } else {
              console.log(`⚠️ Auto-transition failed, manual transition may be required`);
            }
          } catch (error) {
            console.error('Error during auto-transition:', error);
          }
        }
      } else {
        showNotification(response.error || 'Failed to generate email preview', 'error');
      }
    } catch (error: any) {
      showNotification(`Error generating email: ${error.message}`, 'error');
    }
  };

  const sendQuoteRequestEmail = async () => {
    try {
      setSendingEmail(true);
      
      // CRITICAL FIX: For multi-supplier sending, don't pass email content 
      // to let backend auto-detect multiple suppliers and send to all
      // Pass undefined email content to trigger multi-supplier auto-detection
      
      console.log(`📧 [SEND] Multi-supplier mode: letting backend auto-detect all suppliers and send perfect email format to each`);
      
      // Don't pass email content - let backend generate and send to all suppliers
      const response = await temporaryOrderService.sendQuoteRequestEmail(caseId, undefined);
      if (response.success) {
        showNotification('Quote request email sent successfully!', 'success');
        setEmailDialogOpen(false);
        
        // Refresh email history after successful send
        setTimeout(() => {
          loadEmailHistory();
        }, 1000); // Small delay to ensure the backend has processed the email
        
        // DEBUG: Log current workflow state before attempting transition
        console.log(`🔍 DEBUG: Current workflow state: "${currentWorkflowState}"`);
        console.log(`🔍 DEBUG: Expected state for transition: "supplier_contacted"`);
        console.log(`🔍 DEBUG: State match: ${currentWorkflowState === 'supplier_contacted'}`);
        
        // Trigger automatic workflow transition for supplier_contacted → supplier_response_received
        if (currentWorkflowState === 'supplier_contacted') {
          console.log(`🚀 Attempting auto-transition: supplier_contacted → supplier_response_received (Email sent)`);
          try {
            const success = await AutomaticWorkflowService.triggerSupplierEvent(
              caseId,
              currentWorkflowState,
              'EMAIL_RESPONSE_DETECTED',
              {
                action: 'quote_request_email_sent',
                emailData: {
                  subject: emailContent.subject,
                  recipients: emailContent.to?.join(', '),
                  attachments: emailContent.attachments?.length || 0,
                  sentAt: new Date().toISOString()
                },
                temporaryOrderInfo: {
                  positions: temporaryOrder?.positions?.length || 0,
                  totalFiles: getTotalFilesCount()
                }
              }
            );
            
            if (success) {
              console.log(`✅ Auto-transition completed: supplier_contacted → supplier_response_received`);
              // Call onRefresh to update the UI with new workflow state
              onRefresh?.();
            } else {
              console.log(`⚠️ Auto-transition failed, manual transition may be required`);
            }
          } catch (error) {
            console.error('Error during auto-transition:', error);
          }
        }
        
        onEmailSent?.();
      } else {
        showNotification(response.error || 'Failed to send email', 'error');
      }
    } catch (error: any) {
      showNotification(`Error sending email: ${error.message}`, 'error');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleFileDownload = (file: PositionFile) => {
    // Use file_path if available (includes full storage path), otherwise fall back to filename
    const filePath = file.file_path || `uploads/unified/${file.filename}`;
    const url = temporaryOrderService.getFileDownloadUrl(filePath);
    window.open(url, '_blank');
  };

  const formatOrderType = (orderType: string) => {
    return temporaryOrderService.formatOrderType(orderType);
  };

  const formatTypeSpecificData = (data: Record<string, any>, orderType: string) => {
    return temporaryOrderService.formatTypeSpecificData(data, orderType);
  };

  const getTotalFilesCount = () => {
    if (!temporaryOrder) return 0;
    return temporaryOrderService.getTotalFilesCount(temporaryOrder);
  };

  const getOrderStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'warning';
      case 'ready': return 'info';
      case 'sent': return 'primary';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading quote request details...
        </Typography>
      </Box>
    );
  }

  if (!temporaryOrder) {
    return (
      <Alert severity="warning">
        No quote request details found for case {caseNumber || caseId}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Quote Request Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box>
              <Typography variant="h5" gutterBottom display="flex" alignItems="center">
                <AssignmentIcon sx={{ mr: 1 }} />
                Quote Request Details
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Case: {caseNumber || caseId}
              </Typography>
            </Box>
            <Box display="flex" gap={1}>
              <Tooltip title="Refresh data">
                <IconButton onClick={() => { loadTemporaryOrder(); onRefresh?.(); }}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Button
                variant="outlined"
                startIcon={<PreviewIcon />}
                onClick={generateEmailPreview}
              >
                Preview Email
              </Button>
              <Button
                variant="contained"
                startIcon={<SendIcon />}
                onClick={generateEmailPreview}
                color="primary"
              >
                Send Quote Request
              </Button>
            </Box>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="h6" gutterBottom display="flex" alignItems="center">
                  <BusinessIcon sx={{ mr: 1 }} />
                  Order Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Order Type</Typography>
                    <Typography variant="body1">{formatOrderType(temporaryOrder.order_type)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Status</Typography>
                    <Chip 
                      label={temporaryOrder.status.toUpperCase()} 
                      color={getOrderStatusColor(temporaryOrder.status) as any}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Order Date</Typography>
                    <Typography variant="body1">
                      {new Date(temporaryOrder.order_date).toLocaleDateString('de-DE')}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Due Date</Typography>
                    <Typography variant="body1">
                      {new Date(temporaryOrder.due_date).toLocaleDateString('de-DE')}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="h6" gutterBottom display="flex" alignItems="center">
                  <ScheduleIcon sx={{ mr: 1 }} />
                  Quote Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Total Positions</Typography>
                    <Typography variant="body1">{temporaryOrder.positions.length}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Attached Files</Typography>
                    <Typography variant="body1" display="flex" alignItems="center">
                      {getTotalFilesCount()}
                      <AttachFileIcon sx={{ ml: 0.5, fontSize: 16 }} />
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Special Instructions</Typography>
                    <Typography variant="body2">
                      {temporaryOrder.notes || 'No special instructions'}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
          
          {/* Supplier Information Section */}
          {caseDetails?.metadata?.suppliers && Array.isArray(caseDetails.metadata.suppliers) && (
            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12}>
                <Box>
                  <Typography variant="h6" gutterBottom display="flex" alignItems="center">
                    <BusinessIcon sx={{ mr: 1 }} />
                    Selected Suppliers ({caseDetails.metadata.suppliers.length})
                  </Typography>
                  <Grid container spacing={2}>
                    {caseDetails.metadata.suppliers.map((supplier: any, index: number) => (
                      <Grid item xs={12} sm={6} md={4} key={index}>
                        <Card variant="outlined" sx={{ p: 2 }}>
                          <Box display="flex" alignItems="flex-start" gap={1}>
                            <BusinessIcon color="primary" sx={{ mt: 0.5 }} />
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="subtitle2" fontWeight="bold">
                                {supplier.name || 'Unknown Supplier'}
                              </Typography>
                              {supplier.company && (
                                <Typography variant="body2" color="text.secondary">
                                  {supplier.company}
                                </Typography>
                              )}
                              <Typography variant="body2" color="text.secondary" display="flex" alignItems="center" sx={{ mt: 0.5 }}>
                                <EmailIcon sx={{ fontSize: 14, mr: 0.5 }} />
                                {supplier.email || 'No email'}
                              </Typography>
                              {supplier.id && (
                                <Typography variant="caption" color="text.secondary">
                                  ID: {supplier.id}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Positions Details */}
      <Accordion 
        expanded={expandedPanels.has('positions')}
        onChange={() => handlePanelToggle('positions')}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">
            Order Positions ({temporaryOrder.positions.length})
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Position</TableCell>
                  <TableCell>Part Details</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell>Specifications</TableCell>
                  <TableCell align="center">Files</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {temporaryOrder.positions.map((position: TemporaryOrderPosition) => (
                  <TableRow key={position.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        #{position.position_number}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        {position.part_name && (
                          <Typography variant="body2" fontWeight="medium">
                            {position.part_name}
                          </Typography>
                        )}
                        {position.part_number && (
                          <Typography variant="caption" color="text.secondary">
                            Part #: {position.part_number}
                          </Typography>
                        )}
                        {position.notes && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {position.notes}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">{position.quantity}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        {formatTypeSpecificData(position.type_specific_data, temporaryOrder.order_type).map((spec: any, index: number) => (
                          <Typography key={index} variant="caption" display="block">
                            <strong>{spec.label}:</strong> {spec.value}
                          </Typography>
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      {position.files && position.files.length > 0 ? (
                        <Box>
                          <Typography variant="caption" color="primary">
                            {position.files.length} file(s)
                          </Typography>
                          <Box mt={1}>
                            {position.files.map((file: PositionFile) => (
                              <Tooltip key={file.id} title={file.original_filename}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleFileDownload(file)}
                                  color="primary"
                                >
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ))}
                          </Box>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No files
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* Files Summary */}
      {getTotalFilesCount() > 0 && (
        <Accordion 
          expanded={expandedPanels.has('files')}
          onChange={() => handlePanelToggle('files')}
          sx={{ mt: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">
              Attached Files ({getTotalFilesCount()})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <List>
              {temporaryOrder.positions.map((position: TemporaryOrderPosition) =>
                position.files?.map((file: PositionFile) => (
                  <ListItem
                    key={file.id}
                    secondaryAction={
                      <IconButton edge="end" onClick={() => handleFileDownload(file)}>
                        <DownloadIcon />
                      </IconButton>
                    }
                  >
                    <ListItemIcon>
                      <DescriptionIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={file.original_filename}
                      secondary={`Position ${position.position_number} • ${file.file_type} • ${(file.file_size / 1024).toFixed(1)} KB • ${file.description || 'No description'}`}
                    />
                  </ListItem>
                ))
              )}
            </List>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Email History */}
      <Accordion 
        expanded={expandedPanels.has('email-history')}
        onChange={() => handlePanelToggle('email-history')}
        sx={{ mt: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center">
            <HistoryIcon sx={{ mr: 1 }} />
            <Typography variant="h6">
              Email History
              {emails.length > 0 && (
                <Badge 
                  badgeContent={emails.length} 
                  color="primary"
                  sx={{ ml: 2 }}
                />
              )}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" justifyContent="flex-end" mb={2}>
            <Button
              startIcon={<RefreshIcon />}
              onClick={loadEmailHistory}
              disabled={loadingEmails}
            >
              {loadingEmails ? 'Loading...' : 'Refresh'}
            </Button>
          </Box>
          
          {loadingEmails ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                Loading email history...
              </Typography>
            </Box>
          ) : emails.length > 0 ? (
            <List>
              {emails.map((email, index) => (
                <Paper key={index} variant="outlined" sx={{ mb: 2, p: 2 }}>
                  <Box mb={1}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {email.subject}
                    </Typography>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="caption" color="text.secondary">
                        {email.direction === 'outbound' ? 'An: ' : 'Von: '}
                        {email.direction === 'outbound' 
                          ? (Array.isArray(email.to_addresses) 
                              ? email.to_addresses.join(', ') 
                              : email.to_addresses)
                          : email.from_address}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(email.email_date || email.sent_at).toLocaleString('de-DE')}
                      </Typography>
                    </Box>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      whiteSpace: 'pre-wrap',
                      maxHeight: '150px', 
                      overflow: 'auto' 
                    }}
                  >
                    {email.body_text || email.body_html || email.body}
                  </Typography>
                </Paper>
              ))}
            </List>
          ) : (
            <Alert severity="info">
              No email history found for this quote request. Any emails sent will appear here.
            </Alert>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Email Preview Dialog */}
      <Dialog
        open={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <EmailIcon sx={{ mr: 1 }} />
            Quote Request Email Preview
          </Box>
        </DialogTitle>
        <DialogContent>
          {emailContent && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Subject: {emailContent.subject}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                To: {emailContent.to?.join(', ')}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                  {emailContent.body_text}
                </Typography>
              </Paper>
              {emailContent.attachments && emailContent.attachments.length > 0 && (
                <Box mt={2}>
                  <Typography variant="h6" gutterBottom>
                    Attachments ({emailContent.attachments.length})
                  </Typography>
                  <List dense>
                    {emailContent.attachments.map((file: PositionFile, index: number) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <AttachFileIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary={file.original_filename}
                          secondary={`${file.file_type} • ${(file.file_size / 1024).toFixed(1)} KB`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={sendQuoteRequestEmail}
            variant="contained"
            disabled={sendingEmail}
            startIcon={sendingEmail ? <CircularProgress size={16} /> : <SendIcon />}
          >
            {sendingEmail ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QuoteRequestViewer;
